import React, { useState } from 'react'
import * as PropTypes from 'prop-types'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { CardCvcElement, useElements, useStripe } from '@stripe/react-stripe-js'

import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import { makeReservationForClientValidation } from '../helpers'
import PhoneField from '../../FormLegacy/FormFieldsV2/PhoneField'
import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'

import CreditCardInfoForm from './CreditCardInfoForm'

const makeValidationSchema = (t) => {
  const reservationForClientValidation = makeReservationForClientValidation(t)
  return Yup.object().shape({
    guestFirstName: reservationForClientValidation.guestFirstName,
    guestLastName: reservationForClientValidation.guestLastName,
    guestEmail: reservationForClientValidation.guestEmail,
    guestPhone: reservationForClientValidation.guestPhone,
  })
}

const AddReservationGuestForm = ({ onClose, buildingId, noCreditCard }) => {
  const { t } = useTranslation(['reservations'])
  const validationSchema = makeValidationSchema(t)
  const stripe = useStripe()
  const { displaySnackbar } = useSnackbar()
  const elements = useElements()
  const handleSubmit = async (values) => {
    try {
      const userData = {
        firstName: values.guestFirstName,
        lastName: values.guestLastName,
        email: values.guestEmail,
        phone: values.guestPhone,
        buildingId,
      }
      const { id } = await API.createGuest(userData)
      let token
      if (!noCreditCard) {
        const data = { name: creditCardData.cardOwner }
        const cardElement = elements.getElement(CardCvcElement)
        token = (await stripe.createToken(cardElement, data)).token
        await API.setGuestCreditCardData(id, token.id)
      }
      onClose({ guestId: id, ...userData }, token)
    } catch {
      displaySnackbar(t('reservations:addReservationGuestForm.createGuestFailed'), true)
    }
  }
  const [guestFormEligibleForSumbission, setGuestFormEligibleForSumbission] = useState(false)
  const [creditCardData, setCreditCardData] = useState()
  const isCreditCardFormCompleted =
    creditCardData &&
    creditCardData.cardCvc &&
    creditCardData.cardExpiry &&
    creditCardData.cardNumber &&
    creditCardData.cardOwner
  const isEligibleForSubmission =
    !!guestFormEligibleForSumbission && (noCreditCard || isCreditCardFormCompleted)
  return (
    <div className="add-guest-form">
      <Formik
        enableReinitialize
        initialValues={{
          guestFirstName: '',
          guestLastName: '',
          guestEmail: '',
          guestPhone: '',
        }}
        validationSchema={validationSchema}
      >
        {({ handleChange, values, errors, touched, setFieldTouched, isValid, dirty }) => {
          if (guestFormEligibleForSumbission !== (dirty && isValid)) {
            setGuestFormEligibleForSumbission(dirty && isValid)
          }
          return (
            <React.Fragment>
              <div className="columns">
                <div className={'column is-6'}>
                  <div className="add-guest-form__input-label">
                    {t('reservations:addReservationGuestForm.firstName')}
                  </div>
                  <TextField
                    name={'guestFirstName'}
                    placeholder={t('reservations:addReservationGuestForm.firstNamePlaceholder')}
                    value={values.guestFirstName}
                    onChange={handleChange}
                    isError={touched.guestFirstName && !!errors.guestFirstName}
                    errorLabel={errors.guestFirstName}
                    onBlur={() => setFieldTouched('guestFirstName')}
                  />
                </div>
                <div className={'column is-6'}>
                  <div className="add-guest-form__input-label">
                    {t('reservations:addReservationGuestForm.lastName')}
                  </div>
                  <TextField
                    name={'guestLastName'}
                    placeholder={t('reservations:addReservationGuestForm.lastNamePlaceholder')}
                    value={values.guestLastName}
                    onChange={handleChange}
                    isError={touched.guestLastName && !!errors.guestLastName}
                    errorLabel={errors.guestLastName}
                    onBlur={() => setFieldTouched('guestLastName')}
                  />
                </div>
              </div>
              <div className="columns">
                <div className={'column is-6'}>
                  <div className="add-guest-form__input-label">
                    {t('reservations:addReservationGuestForm.email')}
                  </div>
                  <TextField
                    name={'guestEmail'}
                    placeholder={t('reservations:addReservationGuestForm.emailPlaceholder')}
                    value={values.guestEmail}
                    isError={touched.guestEmail && !!errors.guestEmail}
                    errorLabel={errors.guestEmail}
                    onBlur={() => setFieldTouched('guestEmail')}
                    onChange={handleChange}
                  />
                </div>
                <div className={'column is-6'}>
                  <div className="add-guest-form__input-label">
                    {t('reservations:addReservationGuestForm.phoneNumber')}
                  </div>
                  <PhoneField
                    type={'number'}
                    name={'guestPhone'}
                    placeholder={t('reservations:addReservationGuestForm.phoneNumberPlaceholder')}
                    value={values.guestPhone}
                    onChange={handleChange}
                    isError={touched.guestPhone && !!errors.guestPhone}
                    errorLabel={errors.guestPhone}
                    onBlur={() => setFieldTouched('guestPhone')}
                  />
                </div>
              </div>
              {!noCreditCard && (
                <React.Fragment>
                  <div className="add-guest-form__separator" />
                  <CreditCardInfoForm onChange={setCreditCardData} />
                </React.Fragment>
              )}
              <div className="add-guest-form__submit-button-container">
                <button
                  type="submit"
                  onClick={() => handleSubmit(values)}
                  disabled={!isEligibleForSubmission}
                  className="button button-primary"
                >
                  {t('reservations:addReservationGuestForm.addNewGuest')}
                </button>
              </div>
            </React.Fragment>
          )
        }}
      </Formik>
    </div>
  )
}

AddReservationGuestForm.propTypes = {
  onClose: PropTypes.func,
  buildingId: PropTypes.string,
  noCreditCard: PropTypes.bool,
}

export default AddReservationGuestForm
